<template>
 <div ref="scrollRoot" data-tour="attendance-list" class="attendance-tab" :class="contained ? 'h-full overflow-y-auto' : ''">
 <!-- Loading State -->
 <div v-if="isLoading && lessons.length === 0" class="card text-center py-12">
 <div class="animate-pulse">
 <div class="h-4 w-48 bg-stroke dark:bg-stroke-dark rounded mx-auto mb-4"></div>
 <div class="h-3 w-32 bg-stroke dark:bg-stroke-dark rounded mx-auto"></div>
 </div>
 </div>

 <!-- Error State -->
 <div v-else-if="error" class="card border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
 <div class="flex items-center">
 <ExclamationTriangleIcon class="h-5 w-5 text-red-500 dark:text-red-400 mr-3" />
 <div>
 <p class="font-medium text-red-800 dark:text-red-300">Failed to load lessons</p>
 <p class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>
 </div>
 <button @click="loadLessons" class="ml-auto btn-secondary text-sm">Retry</button>
 </div>
 </div>

 <!-- Empty State -->
 <div v-else-if="lessons.length === 0 && !isLoading" class="card text-center py-12">
 <CalendarIcon class="h-12 w-12 mx-auto text-stroke-strong dark:text-stroke-strong-dark mb-3" />
 <p class="text-fg-muted dark:text-fg-muted-dark">No lessons found</p>
 <p class="text-sm text-fg-faint dark:text-fg-faint-dark mt-1">Lessons will appear here when scheduled</p>
 </div>

 <!-- Timeline -->
 <div v-else class="space-y-1">
 <!-- Top sentinel: scroll up → load more future lessons -->
 <div ref="topSentinel"></div>

 <!-- Future loading indicator -->
 <div v-if="isLoadingMoreFuture" class="py-4 text-center">
 <div class="inline-flex items-center text-fg-muted dark:text-fg-muted-dark">
 <ArrowPathIcon class="h-4 w-4 mr-2 animate-spin" />
 <span>Loading more lessons...</span>
 </div>
 </div>

 <div class="space-y-2" style="overflow-anchor: none;">
 <template v-for="item in timelineItems" :key="item.key">
 <!-- Day marker -->
 <div v-if="item.type === 'marker'" class="flex items-center py-1">
 <div class="h-px flex-1 bg-stroke-subtle dark:bg-surface-dark"></div>
 <span class="mx-3 text-xs font-semibold text-fg-faint dark:text-fg-faint-dark uppercase tracking-wide">
 {{ item.label }}
 </span>
 <div class="h-px flex-1 bg-stroke-subtle dark:bg-surface-dark"></div>
 </div>

 <!-- NOW marker -->
 <div v-else-if="item.type === 'now'" data-now-marker class="flex items-center my-1">
 <div class="h-px flex-1 bg-stroke dark:bg-stroke-dark"></div>
 <h3 class="mx-4 text-sm font-semibold text-fg-muted dark:text-fg-muted-dark uppercase tracking-wide">
 {{ currentDateTime }}
 </h3>
 <div class="h-px flex-1 bg-stroke dark:bg-stroke-dark"></div>
 </div>

 <!-- Lesson card -->
 <LessonCard
 v-else
 :lesson="item.data"
 :is-future="item.isFuture"
 @mark-attended="markAttended"
 @mark-excused="markExcused"
 @mark-absent="markAbsent"
 @mark-not-reported="removeAttendance"
 @revert-attendance="revertAttendance"
 />
 </template>
 </div>

 <!-- Past loading indicator -->
 <div v-if="isLoadingMorePast" class="py-4 text-center">
 <div class="inline-flex items-center text-fg-muted dark:text-fg-muted-dark">
 <ArrowPathIcon class="h-4 w-4 mr-2 animate-spin" />
 <span>Loading more lessons...</span>
 </div>
 </div>

 <!-- Bottom sentinel: scroll down → load more past lessons -->
 <div ref="bottomSentinel"></div>
 </div>

 <BravoBurst ref="bravo" />
 </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick, onUnmounted } from 'vue'
import {
 CalendarIcon,
 ArrowPathIcon,
 ExclamationTriangleIcon,
} from '@heroicons/vue/24/outline'
import LessonCard from '../common/LessonCard.vue'
import BravoBurst from '../common/BravoBurst.vue'
import { useApi } from '../../composables/useApi'
import { lessonsChange } from '../../composables/useCalendarData.js'

const props = defineProps({
 teacherName: { type: String, required: true },
 // When true, this component owns its own scroll container (h-full overflow-y-auto)
 // and all scroll math (position preservation, infinite-scroll sentinels, scroll-to-
 // lesson) targets that container instead of the window. Used when the tab is placed
 // in a height-constrained column rather than normal page flow (see App.vue).
 contained: { type: Boolean, default: false }
})

const scrollRoot = ref(null)

// ── Clock ──────────────────────────────────────────────────────────────────
const formatDateTime = () =>
 new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()
 + ' • '
 + new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toUpperCase()

const currentDateTime = ref(formatDateTime())
const currentTime = ref(Date.now()) // reactive clock for nowIndex
let clockInterval = null

// ── State ──────────────────────────────────────────────────────────────────
const lessons = ref([]) // single sorted array: latest → earliest
const futureLessonsCount = ref(0) // how many future lessons are loaded (for pagination offset)
const pastLessonsCount = ref(0) // how many past lessons are loaded (for pagination offset)
const futureHasMore = ref(false)
const pastHasMore = ref(false)
const focusTargetId = ref(null)
const isLoading = ref(false)
const isLoadingMoreFuture = ref(false)
const isLoadingMorePast = ref(false)
const error = ref(null)

const topSentinel = ref(null)
const bottomSentinel = ref(null)
let topObserver = null
let bottomObserver = null

const { fetchUserLessonsTimeline } = useApi()

// ── Time helpers ───────────────────────────────────────────────────────────
// Parse "3:00 PM" + "2026-04-15" into a local Date
const getLessonStartTime = (lesson) => {
 const timeStr = lesson.lesson_time
 if (!timeStr) return new Date(lesson.lesson_date + 'T00:00:00')
 const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
 if (!match) return new Date(lesson.lesson_date + 'T00:00:00')
 let hours = parseInt(match[1])
 const minutes = parseInt(match[2])
 const period = match[3].toUpperCase()
 if (period === 'PM' && hours !== 12) hours += 12
 if (period === 'AM' && hours === 12) hours = 0
 return new Date(`${lesson.lesson_date}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`)
}

// ── nowIndex ───────────────────────────────────────────────────────────────
// Index of the first lesson whose start time is before now.
// Lessons before this index are future; from this index onward are past.
// Updates reactively every minute via currentTime.
const nowIndex = computed(() => {
 const now = currentTime.value
 const idx = lessons.value.findIndex(l => getLessonStartTime(l).getTime() < now)
 return idx === -1 ? lessons.value.length : idx
})

// ── Day labels ─────────────────────────────────────────────────────────────
const getDayLabel = (dateStr) => {
 const today = new Date()
 today.setHours(0, 0, 0, 0)
 const date = new Date(dateStr + 'T00:00:00')
 date.setHours(0, 0, 0, 0)
 const diffDays = Math.round((date - today) / (1000 * 60 * 60 * 24))
 if (diffDays === 0) return 'TODAY'
 if (diffDays === 1) return 'TOMORROW'
 if (diffDays === -1) return 'YESTERDAY'
 if (Math.abs(diffDays) < 7) return date.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()
 return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()
}

// ── timelineItems ──────────────────────────────────────────────────────────
// Flat list of { type: 'lesson'|'marker'|'now' } items for the v-for.
// Latest lesson at the top, NOW marker injected at the right position,
// day labels inserted before the first card of each date group.
const timelineItems = computed(() => {
 const items = []
 let currentDate = null
 let nowInserted = false

 lessons.value.forEach((lesson, i) => {
 // Inject NOW marker before the first past lesson
 if (i === nowIndex.value && !nowInserted) {
 items.push({ type: 'now', key: 'now-marker' })
 nowInserted = true
 }

 if (lesson.lesson_date !== currentDate) {
 currentDate = lesson.lesson_date
 items.push({ type: 'marker', label: getDayLabel(currentDate), date: currentDate, key: `marker-${currentDate}` })
 }

 items.push({ type: 'lesson', data: lesson, isFuture: i < nowIndex.value, key: `lesson-${lesson.id}` })
 })

 // NOW marker at the end when all lessons are in the future
 if (!nowInserted) {
 items.push({ type: 'now', key: 'now-marker' })
 }

 return items
})

// ── Load lessons ───────────────────────────────────────────────────────────
const loadLessons = async () => {
 isLoading.value = true
 error.value = null

 try {
 const data = await fetchUserLessonsTimeline(30, 30, 0, 0)

 const futureLessons = [...(data.future_lessons || [])].reverse() // ASC→DESC for unified list
 const pastLessons = data.past_lessons || [] // already DESC

 lessons.value = [...futureLessons, ...pastLessons]
 futureLessonsCount.value = futureLessons.length
 pastLessonsCount.value = pastLessons.length
 futureHasMore.value = data.future_has_more || false
 pastHasMore.value = data.past_has_more || false
 focusTargetId.value = data.focus_target_id || null

 nextTick(() => {
 teardownObservers()
 requestAnimationFrame(() => {
 requestAnimationFrame(() => {
 scrollToFocusTarget()
 setupObservers()
 })
 })
 })

 } catch (err) {
 error.value = err.message || 'Failed to load lessons'
 console.error('Error loading lessons:', err)
 } finally {
 isLoading.value = false
 }
}

// ── Pagination ─────────────────────────────────────────────────────────────
const loadMoreFuture = async () => {
 if (isLoadingMoreFuture.value || !futureHasMore.value) return
 if (topObserver) { topObserver.disconnect(); topObserver = null }

 isLoadingMoreFuture.value = true
 const scrollHeightBefore = props.contained ? (scrollRoot.value?.scrollHeight ?? 0) : document.documentElement.scrollHeight

 try {
 const data = await fetchUserLessonsTimeline(0, 10, 0, futureLessonsCount.value)
 const newFuture = [...(data.future_lessons || [])].reverse()
 lessons.value = [...newFuture, ...lessons.value]
 futureLessonsCount.value += newFuture.length
 futureHasMore.value = data.future_has_more || false

 await nextTick()
 if (props.contained) {
 const delta = (scrollRoot.value?.scrollHeight ?? 0) - scrollHeightBefore
 if (delta > 0) scrollRoot.value?.scrollBy({ top: delta, behavior: 'instant' })
 } else {
 const delta = document.documentElement.scrollHeight - scrollHeightBefore
 if (delta > 0) window.scrollBy({ top: delta, behavior: 'instant' })
 }
 } catch (err) {
 console.error('Error loading more future lessons:', err)
 } finally {
 isLoadingMoreFuture.value = false
 await nextTick()
 setupObservers()
 }
}

const loadMorePast = async () => {
 if (isLoadingMorePast.value || !pastHasMore.value) return
 if (bottomObserver) { bottomObserver.disconnect(); bottomObserver = null }

 isLoadingMorePast.value = true
 try {
 const data = await fetchUserLessonsTimeline(10, 0, pastLessonsCount.value, 0)
 const newPast = data.past_lessons || []
 lessons.value = [...lessons.value, ...newPast]
 pastLessonsCount.value += newPast.length
 pastHasMore.value = data.past_has_more || false
 } catch (err) {
 console.error('Error loading more past lessons:', err)
 } finally {
 isLoadingMorePast.value = false
 await nextTick()
 setupObservers()
 }
}

// ── IntersectionObserver ───────────────────────────────────────────────────
const setupObservers = () => {
 const root = props.contained ? scrollRoot.value : null
 if (topSentinel.value && futureHasMore.value) {
 topObserver = new IntersectionObserver(
 (entries) => { if (entries[0].isIntersecting) loadMoreFuture() },
 { root, rootMargin: '0px', threshold: 0 }
 )
 topObserver.observe(topSentinel.value)
 }
 if (bottomSentinel.value && pastHasMore.value) {
 bottomObserver = new IntersectionObserver(
 (entries) => { if (entries[0].isIntersecting) loadMorePast() },
 { root, rootMargin: '0px', threshold: 0 }
 )
 bottomObserver.observe(bottomSentinel.value)
 }
}

const teardownObservers = () => {
 if (topObserver) { topObserver.disconnect(); topObserver = null }
 if (bottomObserver) { bottomObserver.disconnect(); bottomObserver = null }
}

// ── Scroll helpers ─────────────────────────────────────────────────────────
const scrollToElementWithOffset = (element, offset = 90, behavior = 'smooth') => {
 if (!element) return
 if (props.contained && scrollRoot.value) {
 const container = scrollRoot.value
 const elRect = element.getBoundingClientRect()
 const containerRect = container.getBoundingClientRect()
 const relativeTop = elRect.top - containerRect.top + container.scrollTop
 const visibleHeight = container.clientHeight - offset
 const targetScrollTop = relativeTop + elRect.height - visibleHeight * 0.75
 container.scrollTo({ top: Math.max(0, targetScrollTop), behavior })
 } else {
 const rect = element.getBoundingClientRect()
 const absoluteTop = rect.top + window.pageYOffset
 const visibleHeight = window.innerHeight - offset
 const targetScrollTop = absoluteTop + rect.height - visibleHeight * 0.75
 window.scrollTo({ top: Math.max(0, targetScrollTop), behavior })
 }
}

const scrollToLesson = (lessonId, behavior = 'smooth') => {
 const card = document.querySelector(`[data-lesson-id="${lessonId}"]`)
 if (!card) return
 const target = card.querySelector('[data-attendance-buttons]') ?? card
 scrollToElementWithOffset(target, 90, behavior)
}

const scrollToFocusTarget = () => {
 if (focusTargetId.value) {
 scrollToLesson(focusTargetId.value, 'instant')
 } else {
 const nowEl = document.querySelector('[data-now-marker]')
 if (nowEl) scrollToElementWithOffset(nowEl, 90, 'instant')
 }
}

// ── Attendance updates ─────────────────────────────────────────────────────
const updateStudentAttendance = (lessonId, studentName, status) => {
 const lesson = lessons.value.find(l => l.id === lessonId)
 if (!lesson) return
 if (Array.isArray(lesson.attendance)) {
 const student = lesson.attendance.find(s => s.name === studentName)
 if (student) student.status = status
 } else {
 lesson.attendance_status = status
 }
}

const lessonNeedsAttendance = (lesson) => {
 if (Array.isArray(lesson.attendance)) {
 if (lesson.attendance.length === 0) return false
 return lesson.attendance.some(
 s => !['attended', 'excused', 'absent'].includes(s.status ?? s.attendance ?? 'not reported')
 )
 }
 return !['attended', 'excused', 'absent'].includes(lesson.attendance_status ?? 'not reported')
}

const updateFocusTarget = () => {
 const oldest = lessons.value.slice(nowIndex.value).findLast(lessonNeedsAttendance)
 focusTargetId.value = oldest?.id ?? null
}

// "Bravo!" when a mark clears the last unmarked past lesson — at most once a day per browser.
const bravo = ref(null)
const pastNeedsAttendance = () => lessons.value.slice(nowIndex.value).some(lessonNeedsAttendance)
const celebrateIfCaughtUp = (hadPending) => {
 if (!hadPending || pastNeedsAttendance()) return
 const key = 'attendance-bravo-day'
 const today = new Date().toDateString()
 try {
 if (localStorage.getItem(key) === today) return
 localStorage.setItem(key, today)
 } catch { /* storage blocked — celebrate anyway */ }
 bravo.value?.play()
}

const markAndScroll = (lessonId, studentName, status) => {
 const hadPending = pastNeedsAttendance()
 updateStudentAttendance(lessonId, studentName, status)
 celebrateIfCaughtUp(hadPending)
 nextTick(() => {
 updateFocusTarget()
 scrollToLesson(focusTargetId.value, 'smooth')
 })
}

const revertAttendance = (lessonId, studentName, previousStatus) =>
 updateStudentAttendance(lessonId, studentName, previousStatus)

const markAttended = (lessonId, studentName) => markAndScroll(lessonId, studentName, 'attended')
const markExcused = (lessonId, studentName) => markAndScroll(lessonId, studentName, 'excused')
const markAbsent = (lessonId, studentName) => markAndScroll(lessonId, studentName, 'absent')
const removeAttendance = (lessonId, studentName) =>
 updateStudentAttendance(lessonId, studentName, 'not reported')

// ── Lifecycle ──────────────────────────────────────────────────────────────
const refreshData = () => {
 teardownObservers()
 loadLessons()
}

onMounted(() => {
 if (!props.contained && 'scrollRestoration' in history) history.scrollRestoration = 'manual'
 loadLessons()
 clockInterval = setInterval(() => {
 currentDateTime.value = formatDateTime()
 currentTime.value = Date.now()
 }, 60000)
})

onUnmounted(() => {
 teardownObservers()
 if (clockInterval) clearInterval(clockInterval)
})

watch(() => props.teacherName, refreshData)
// Attendance marks made inside this tab are already applied optimistically (and a reload
// would re-scroll the list), so only react to changes that came from elsewhere.
watch(lessonsChange, ({ attendanceOnly }) => { if (!attendanceOnly) refreshData() })

defineExpose({ loadLessons, refreshData, scrollToLesson })
</script>

<style scoped>
.attendance-tab {
 @apply pb-6;
}

.btn-secondary {
 @apply px-3 py-1.5 text-sm font-medium rounded-md border border-stroke-strong dark:border-stroke-strong-dark
 bg-surface dark:bg-surface-dark text-fg-secondary dark:text-fg-secondary-dark
 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500;
}
</style>
